import classNames from "classnames"

import { getImageDimensionsFromUrl } from "@utils/image"
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  Children,
} from "react"

/*
  TODO: use Tailwind's resolveConfig function to get these breakpoints from
  our Tailwind config.

  Read more here: https://tailwindcss.com/docs/configuration#referencing-in-java-script
  Comment thread here: https://gitlab.com/yoco/yoco-websites/yoco-com/-/merge_requests/1359#note_1765618069
*/
enum Breakpoint {
  default = "default",
  "sm" = 667,
  "md" = 900,
  "lg" = 1200,
  "xl" = 1512,
}

type columnsPerBreakpoint = {
  [key in Breakpoint]?: number
}

type Props = {
  columnsPerBreakpoint?: columnsPerBreakpoint
  mobileColumns?: number // deprecated - use columnsPerBreakpoint instead
  columnClassName?: string
  multiColumnClassName?: string // applies these classNames responsively when number of columns is greater than 1
  className?: string
  children: React.ReactNode
}

const DEFAULT_COLUMNS = 2
const MEASUREMENT_INTERVAL_MS = 100
const MAX_MEASUREMENT_ATTEMPTS = 10
const MEASURING_STYLE: React.CSSProperties = {
  height: 0,
  overflow: "hidden",
  visibility: "hidden",
}

const Masonry = ({
  /*
      columnsPerBreakpoint works on a desktop-first policy for
      breakpoints which is why "default" refers to desktop screens
      and "667" refers to any screen smaller than 667px.
    */
  mobileColumns = 1,
  columnsPerBreakpoint = {
    default: DEFAULT_COLUMNS,
    [Breakpoint.sm]: mobileColumns,
  },
  columnClassName,
  multiColumnClassName,
  className,
  children,
  ...props
}: Props) => {
  const [columnCount, setColumnCount] = useState<number>(
    getCurrentBreakpointValue(columnsPerBreakpoint)
  )
  const elementHeightsRef = useRef<number[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const [isArranged, setIsArranged] = useState(false)
  /*
    Children can only be placed into columns once their heights are known, so they
    are first rendered into a single column purely so they can be measured. This
    column has to stay in normal flow: a container that sizes to its content (eg a
    grid item with justify-self) collapses to zero width without an in-flow child,
    and every child would then measure zero height. The container itself is hidden
    while measuring instead (see MEASURING_STYLE), so the un-arranged layout is
    never painted and never reserves space - previously it was painted, which is
    what caused the shift on first load once the columns were calculated.
  */
  const [masonryItems, setMasonryItems] = useState(
    <div
      style={{
        width: `${100 / columnCount}%`,
      }}
      className={columnClassName}
    >
      {children}
    </div>
  )

  const handleBreakpointChange = () => {
    setColumnCount(getCurrentBreakpointValue(columnsPerBreakpoint))
  }

  useEffect(() => {
    handleBreakpointChange()

    globalThis.addEventListener("resize", handleBreakpointChange, {
      passive: true,
    })
    /*
      TODO - maybe re-arrange elements on window resize since the height of elements change
    */

    return () => {
      globalThis.removeEventListener("resize", handleBreakpointChange)
    }
  }, [])

  type MappedElements = React.ReactNode[]
  type MappedColumns = MappedElements[]

  const mapElements = useCallback((): MappedColumns => {
    const columns: MappedColumns = Array.from({ length: columnCount }, () => [])

    if (!elementHeightsRef.current || elementHeightsRef.current.length < 1)
      throw new Error("Failed to arrange Masonry elements. Missing elementsRef")

    const childrenArray = Children.toArray(children)

    const columnHeights = Array.from({ length: columnCount }, () => 0)
    let columnIndex = 0

    for (let index = 0; index < childrenArray.length; index++) {
      columnIndex = columnHeights.indexOf(Math.min(...columnHeights))

      columns[columnIndex].push(childrenArray[index])
      columnHeights[columnIndex] += elementHeightsRef.current[index]
    }

    return columns
  }, [columnCount, children])

  const renderColumns = useCallback(() => {
    const columns = mapElements()

    const columnWidth = `${100 / columns.length}%`

    const columnAttributes = {
      style: {
        width: columnWidth,
      },
      className: classNames(
        columns.length > 1 ? multiColumnClassName : "",
        columnClassName
      ),
    }

    return (
      <>
        {columns.map((column, index) => {
          return (
            <div {...columnAttributes} key={`masonry-column-${index}`}>
              {column.map((element) => element)}
            </div>
          )
        })}
      </>
    )
  }, [columnClassName, multiColumnClassName, mapElements])

  useLayoutEffect(() => {
    const childrenLength =
      children && typeof children === "object"
        ? Object.keys(children).length
        : NaN

    if (Number.isNaN(childrenLength)) {
      throw new Error("Unhandled children type in Masonry")
    }

    const measureAndArrange = (acceptPartialHeights = false) => {
      if (
        containerRef.current &&
        containerRef.current.children[0]?.children.length === childrenLength
      ) {
        const elements: number[] = []

        for (const column of Array.from(containerRef.current.children)) {
          for (const element of Array.from(column.children)) {
            /*
            TODO - handle images more generically and add handling for images in flex-row
            also somehow ignore SVGs
            */
            // Hack to get height of image before it's fully loaded.
            if (
              element.children.length === 2 &&
              element.children[1].tagName === "IMG"
            ) {
              // Hack. Assumes no image if true
              const imageSrc = element.children[1].getAttribute("src")

              if (imageSrc) {
                const imgDimensions = getImageDimensionsFromUrl(imageSrc)

                elements.push(
                  element.children[0].getBoundingClientRect().height +
                    Number.parseInt(imgDimensions.height || "0")
                )
                continue
              }

              elements.push(element.getBoundingClientRect().height)
              continue
            }

            elements.push(element.getBoundingClientRect().height)
          }
        }

        /*
          A zero height means that child has not been laid out yet, and arranging on
          those would pile every child into the first column - measure again instead,
          unless we have run out of attempts and showing something is better.
        */
        const isFullyMeasured = elements.every((height) => height > 0)

        if (elements.length > 0 && (isFullyMeasured || acceptPartialHeights)) {
          elementHeightsRef.current = elements
          setMasonryItems(renderColumns())
          setIsArranged(true)
          return true
        }
      }

      return false
    }

    /*
      Measure as soon as the children are committed to the DOM so the arranged
      columns paint in the same frame where possible, falling back to polling for
      children that are not laid out yet.
    */
    if (measureAndArrange()) return

    let attempts = 0

    const interval = setInterval(() => {
      attempts += 1
      const isFinalAttempt = attempts >= MAX_MEASUREMENT_ATTEMPTS

      if (measureAndArrange(isFinalAttempt) || isFinalAttempt) {
        clearInterval(interval)
        /*
          Nothing is visible until this flips, so give up on measuring rather than
          leaving the grid hidden - an un-arranged grid beats a missing one.
        */
        setIsArranged(true)
      }
    }, MEASUREMENT_INTERVAL_MS)

    return () => {
      clearInterval(interval)
    }
  }, [containerRef, children, renderColumns])

  useEffect(() => {
    if (elementHeightsRef.current && elementHeightsRef.current.length > 0) {
      setMasonryItems(renderColumns())
    }
  }, [elementHeightsRef, renderColumns])

  return (
    <div
      {...props}
      className={className}
      style={isArranged ? undefined : MEASURING_STYLE}
      ref={containerRef}
    >
      {masonryItems}
    </div>
  )
}

const getCurrentBreakpointValue = (
  columnsPerBreakpoint: columnsPerBreakpoint
) => {
  let matchedBreakpoint = Infinity
  let columns = columnsPerBreakpoint.default || DEFAULT_COLUMNS
  const browserWidth =
    typeof document !== "undefined" ? document.body.clientWidth : 0

  for (const breakpoint in columnsPerBreakpoint) {
    const currentBreakpoint = Number.parseInt(breakpoint)
    const isCurrentBreakpoint =
      currentBreakpoint > 0 && browserWidth <= currentBreakpoint

    if (isCurrentBreakpoint && currentBreakpoint < matchedBreakpoint) {
      matchedBreakpoint = currentBreakpoint
      const breakpointValue = columnsPerBreakpoint[breakpoint as Breakpoint]
      columns = breakpointValue ? breakpointValue : columns
    }
  }

  columns = Math.max(1, columns || 1)

  return columns
}

export default Masonry
